import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from '../utils/toast';
import { UserX, UserCheck, Trash2, RefreshCw, Users, Building2, ClipboardList, Plus, Edit } from 'lucide-react';
import axios from 'axios';
import ReassignmentModal from './ReassignmentModal';
import AddEditUserModal from './AddEditUserModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagementTab = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Reassignment modal state
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reassignType, setReassignType] = useState(null); // 'leads' or 'hospitals'
  
  // Add/Edit User modal state
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [officers, setOfficers] = useState([]);
  const [defaultOwnerName, setDefaultOwnerName] = useState('Exlva CRM Officer');

  useEffect(() => {
    fetchUsers();
  }, [statusFilter]);

  useEffect(() => {
    fetchDefaultOwner();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, searchQuery, roleFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const includeDeactivated = statusFilter === 'all' || statusFilter === 'deactivated';
      
      const response = await axios.get(`${API}/crm/users/all?include_deactivated=${includeDeactivated}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch CRM users');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...users];
    
    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(u => u.is_active);
    } else if (statusFilter === 'deactivated') {
      filtered = filtered.filter(u => !u.is_active);
    }
    
    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }
    
    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(u => 
        u.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredUsers(filtered);
  };

  const fetchOfficers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crm/users/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const officersList = response.data.filter(u => u.role === 'CRM Officer' && u.is_active);
      setOfficers(officersList);
    } catch (error) {
      console.error('Failed to fetch officers:', error);
    }
  };

  const fetchDefaultOwner = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crm/default-owner`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDefaultOwnerName(response.data?.owner_name || 'Exlva CRM Officer');
    } catch (error) {
      console.error('Failed to fetch default lead owner:', error);
      setDefaultOwnerName('Exlva CRM Officer');
    }
  };

  const handleAddUser = () => {
    fetchOfficers(); // Fetch latest officers list
    setEditingUser(null);
    setShowAddEditModal(true);
  };

  const handleEditUser = (user) => {
    fetchOfficers(); // Fetch latest officers list
    setEditingUser(user);
    setShowAddEditModal(true);
  };

  const handleModalSuccess = () => {
    fetchUsers();
  };

  const handleDeactivate = async (userId, userName, userRole) => {
    const message = userRole === 'CRM Officer' 
      ? `Are you sure you want to deactivate ${userName}? Their associates will need to be reassigned.`
      : `Are you sure you want to deactivate ${userName}?`;
    
    if (!window.confirm(message)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/crm/users/${userId}/deactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.requires_reassignment) {
        toast.warning(`${userName} deactivated. ${response.data.associates_count} associates need reassignment.`);
      } else {
        toast.success(response.data.message);
      }
      
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate user');
    }
  };

  const handleReactivate = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to reactivate ${userName}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/crm/users/${userId}/reactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${userName} reactivated successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate user');
    }
  };

  const handleDelete = async (userId, userName, leadCount, hospitalCount) => {
    if (leadCount > 0 || hospitalCount > 0) {
      toast.error(`Cannot delete ${userName}. Please reassign their ${leadCount} leads and ${hospitalCount} hospitals first.`);
      return;
    }
    
    if (!window.confirm(`Are you sure you want to permanently delete ${userName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/crm/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${userName} deleted successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openReassignModal = (user, type) => {
    setSelectedUser(user);
    setReassignType(type);
    setShowReassignModal(true);
  };

  const handleReassignComplete = () => {
    setShowReassignModal(false);
    setSelectedUser(null);
    setReassignType(null);
    fetchUsers();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add User Button */}
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-gray-600">
          Default lead owner: <span className="font-semibold text-[#0056B3]">{defaultOwnerName}</span>
        </div>
        <Button 
          onClick={handleAddUser}
          className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="deactivated">Deactivated Only</SelectItem>
                <SelectItem value="all">All Users</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="CRM Officer">CRM Officers</SelectItem>
                <SelectItem value="CRM Associate">CRM Associates</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-end">
            <Button 
              onClick={fetchUsers} 
              variant="outline" 
              className="w-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* User Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>User</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Leads</TableHead>
                <TableHead className="text-center">Hospitals</TableHead>
                <TableHead className="text-center">Associates</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div>
                        <div className="font-medium text-gray-900">{user.full_name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-medium">
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {user.is_active ? (
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800">Deactivated</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <span className="font-medium">{user.lead_count || 0}</span>
                        {user.lead_count > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openReassignModal(user, 'leads')}
                            className="h-6 px-2"
                          >
                            <ClipboardList className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <span className="font-medium">{user.hospital_count || 0}</span>
                        {user.hospital_count > 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openReassignModal(user, 'hospitals')}
                            className="h-6 px-2"
                          >
                            <Building2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {user.role === 'CRM Officer' ? (
                        <span className="font-medium">{user.associate_count || 0}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditUser(user)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        
                        {user.is_active ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeactivate(user.id, user.full_name, user.role)}
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReactivate(user.id, user.full_name)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            Reactivate
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(user.id, user.full_name, user.lead_count, user.hospital_count)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={user.lead_count > 0 || user.hospital_count > 0}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <p className="text-sm text-blue-800">
          <strong>Showing {filteredUsers.length} of {users.length} users</strong>
          {' • '}
          {filteredUsers.filter(u => u.is_active).length} active
          {' • '}
          {filteredUsers.filter(u => !u.is_active).length} deactivated
        </p>
      </div>

      {/* Reassignment Modal */}
      {showReassignModal && selectedUser && (
        <ReassignmentModal
          isOpen={showReassignModal}
          onClose={() => setShowReassignModal(false)}
          sourceUser={selectedUser}
          type={reassignType}
          onComplete={handleReassignComplete}
        />
      )}

      {/* Add/Edit User Modal */}
      <AddEditUserModal
        isOpen={showAddEditModal}
        onClose={() => setShowAddEditModal(false)}
        onSuccess={handleModalSuccess}
        editUser={editingUser}
        officers={officers}
      />
    </div>
  );
};

export default UserManagementTab;
